/**
 * Back closes an open BottomSheet (Figma 11611:8319, dev note 11702:19408).
 * Kept out of BottomSheet.tsx so that file exports only the component.
 */
import { useEffect, useRef } from 'react';

/** Marks the history entry an open sheet pushes, so Back can close it. */
const HISTORY_KEY = 'yarutaBottomSheet';

function historyState(): Record<string, unknown> {
  const state: unknown = window.history.state;
  return state !== null && typeof state === 'object'
    ? (state as Record<string, unknown>)
    : {};
}

function onSheetEntry(): boolean {
  return historyState()[HISTORY_KEY] === true;
}

/**
 * True while an open sheet's own history entry is on top. A sheet action
 * that navigates away (Add to Yaruta's Type it in) navigates with
 * `{ replace: true }` when it is, so the new page takes the sheet's entry
 * instead of stacking on it: one Back then returns to the page under the
 * sheet, the next leaves it, exactly as before the sheet existed.
 */
export function isBottomSheetHistoryEntry(): boolean {
  return onSheetEntry();
}

function pushSheetEntry() {
  window.history.pushState({ ...historyState(), [HISTORY_KEY]: true }, '');
}

/**
 * The `history.back()` that removes a closed sheet's entry, deferred one
 * task. A sheet that re-opens before it runs (StrictMode's mount → unmount →
 * mount, in dev) cancels it and keeps the entry instead of pushing a second.
 */
let pendingPop: ReturnType<typeof setTimeout> | null = null;
/**
 * Our own `history.back()` is on its way (traversal is async). A sheet
 * opened meanwhile must not mistake that pop for the parent's Back, and
 * pushes its entry only once the pop has landed.
 */
let popInFlight = false;
let pushAfterPop: (() => void) | null = null;
let ownPop: Event | null = null;

function popSheetEntry() {
  if (!onSheetEntry()) return;
  popInFlight = true;
  window.addEventListener(
    'popstate',
    (e) => {
      popInFlight = false;
      ownPop = e;
      const push = pushAfterPop;
      pushAfterPop = null;
      push?.();
    },
    { once: true },
  );
  window.history.back();
}

/**
 * Back closes the sheet (Figma 11611:8319, dev note 11702:19408): opening
 * pushes one entry on the same URL, carrying the router's own state so React
 * Router sees no navigation. Back pops it and closes. Any other close pops it
 * too — unless the close navigated first (Type it in), in which case the new
 * page is on top and the stale entry is left alone.
 */
export function useBackCloses(open: boolean, onClose: () => void) {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    if (pendingPop !== null) {
      clearTimeout(pendingPop);
      pendingPop = null;
    }
    const push = () => pushSheetEntry();
    if (popInFlight) pushAfterPop = push;
    else if (!onSheetEntry()) push();

    const onPop = (e: PopStateEvent) => {
      if (e === ownPop) return;
      if (!onSheetEntry()) onCloseRef.current();
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      if (pushAfterPop === push) pushAfterPop = null;
      pendingPop = setTimeout(() => {
        pendingPop = null;
        popSheetEntry();
      }, 0);
    };
  }, [open]);
}
